import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  Monitor,
  Music,
  Laugh,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { useTheme } from "../contexts/ThemeContext";

const ThemeSidebar = () => {
  const {
    currentTheme,
    themeConfig,
    changeTheme,
    labels,
    selectedLabel,
    selectLabel,
    isProductionDomain,
    isSidebarOpen,
    toggleSidebar,
  } = useTheme();

  const [isLabelSectionOpen, setIsLabelSectionOpen] = useState(true);
  const [isThemeSectionOpen, setIsThemeSectionOpen] = useState(true);

  const themeIcons = {
    rock: Monitor,
    latino: Music,
    standup: Laugh,
    fabiomeyer: Monitor,
    spkombat: Monitor, // Ajout d'une icône valide pour spkombat
    jemmyx: Monitor,
    sport: Monitor,
    wildpeaks: Monitor,
  };

  const themeNames = {
    fabiomeyer: "HOME",
    rock: "Rock & Metal",
    latino: "Latino Music",
    standup: "Stand-Up Comedy",
    spkombat: "SP Kombat", // Ajout du nom pour spkombat
    jemmyx: "Jemmyx",
    sport: "Sport",
    wildpeaks: "Wild Peaks",
  };

  const themeColors = {
  rock: "from-red-600 to-red-800",
  spkombat: "from-red-600 to-red-800",
  latino: "from-orange-500 to-orange-700",
  standup: "from-yellow-500 to-yellow-700",
  fabiomeyer: "from-blue-500 to-blue-700",
  jemmyx: "from-purple-500 to-purple-700",
  sport: "from-green-500 to-green-700",
  wildpeaks: "from-green-500 to-green-700",
  };

  const isOpen = isSidebarOpen; // Pour éviter les erreurs dans les références existantes

  const handleThemeChange = (newTheme) => {
    changeTheme(newTheme);
    // Animation douce : petit délai avant de fermer la sidebar pour voir le changement
    setTimeout(() => {
      toggleSidebar(); // Utiliser toggleSidebar pour fermer la barre latérale
    }, 800);
  };

  const handleLabelSelect = (label) => {
    selectLabel(label);
    // Fermer la sidebar après la sélection d'un label
    setTimeout(() => {
      toggleSidebar(); // Utiliser toggleSidebar pour fermer la barre latérale
    }, 500);
  };

  const handleClearFilter = () => {
    selectLabel(null);
    // Fermer la sidebar après avoir effacé le filtre
    setTimeout(() => {
      toggleSidebar(); // Utiliser toggleSidebar pour fermer la barre latérale
    }, 300);
  };

  const CurrentIcon = themeIcons[currentTheme] || Monitor;

  // Calculer la hauteur dynamique pour la liste des labels si le switch theme est masqué
  const labelListMaxHeight = !isProductionDomain ? 'max-h-64' : 'h-[calc(100vh-220px)]';
  const isThemesVisible = window.location.hostname === 'jemmyx.com' || window.location.hostname === 'localhost';

  return (
    <>
      {/* Toggle Button */}
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-[110]">
        <Button
          onClick={toggleSidebar}
          className={`
            p-3 w-10 h-10 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110
            bg-gradient-to-r ${themeColors[currentTheme]} text-white
            ${isOpen ? "translate-x-0" : "translate-x-0"}
          `}
          size="sm"
        >
          {isOpen ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`
          fixed right-0 top-0 h-full w-96 bg-black/95 backdrop-blur-md border-l border-white/10 
          transform transition-transform duration-500 ease-in-out z-[100]
          ${isOpen ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            {/* Theme Logo */}
            <div className="mb-4 text-center">
              <img
                src={themeConfig.branding.logo}
                alt={`${themeConfig.name} Logo`}
                className="h-12 mx-auto transition-all duration-300"
              />
            </div>

            <Button
              onClick={toggleSidebar}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Current Theme Section */}
        <div className="p-2 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">
            <CurrentIcon className="w-5 h-5" />
            {themeNames[currentTheme]}
          </h2>

          {/* Labels and Themes in Columns */}
          {/* only padding right */}
          <div className="flex flex-row gap-4" style={{ padding: '1rem 0.5rem 1rem 0rem' }}>
            {/* Labels Column */}
            <div id="labels-column" className="flex-1 border-r border-white/10 pr-4 overflow-y-auto max-h-[calc(100vh-220px)]">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setIsLabelSectionOpen(!isLabelSectionOpen)}
              >
                <h2 className="text-xl font-bold text-white">Categories</h2>
                <span>{isLabelSectionOpen ? "-" : "+"}</span>
              </div>
              {isLabelSectionOpen && (
                <div className="space-y-2 mt-4">
                  {/* Existing label list rendering logic */}
                  {labels.map((label, index) => {
                    const isSelected = selectedLabel?.id === label.id;
                    return (
                      <div
                        key={label.id}
                        onClick={() => handleLabelSelect(label)}
                        className={`
                          p-2 rounded-lg text-white text-sm cursor-pointer
                          transform transition-all duration-300 hover:scale-105
                          animate-fade-in
                          ${
                            isSelected
                              ? "bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg border border-blue-400"
                              : "bg-white/5 hover:bg-white/10 border border-transparent"
                          }
                        `}
                        style={{
                          animationDelay: `${index * 50}ms`,
                        }}
                      >
                        <div className="font-medium flex items-center justify-between">
                          {label.title}
                          {isSelected && (
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          )}
                        </div>
                        {false && (
                        <div
                          className={`text-xs ${
                            isSelected ? "text-blue-100" : "text-gray-400"
                          }`}
                        >
                          {label.description}
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Themes Column */}
            {isThemesVisible && (
              <div id="themes-column" className="flex-1 pl-0">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setIsThemeSectionOpen(!isThemeSectionOpen)}
                >
                  <h3 className="text-lg font-semibold text-white">Channels</h3>
                  <span>{isThemeSectionOpen ? "-" : "+"}</span>
                </div>
                {isThemeSectionOpen && (
                  <div className="space-y-3 mt-4">
                    {/* Existing theme list rendering logic */}
                    {Object.entries(themeNames).map(([themeKey, themeName]) => {
                      const Icon = themeIcons[themeKey];
                      const isActive = currentTheme === themeKey;

                      return (
                        <Button
                          key={themeKey}
                          onClick={() => handleThemeChange(themeKey)}
                          disabled={isActive}
                          className={`
                            w-full p-4 justify-start gap-3 transition-all duration-300 transform hover:scale-105
                            ${
                              isActive
                                ? `bg-gradient-to-r ${themeColors[themeKey]} text-white cursor-default animate-pulse-glow`
                                : "bg-white/5 text-white hover:bg-white/10"
                            }
                          `}
                          variant={isActive ? "default" : "ghost"}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="font-medium">{themeName}</span>
                          {isActive && (
                            <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse" />
                          )}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Theme Info */}
        {false && themeConfig.branding && (
          <div className="p-6 mt-auto">
            <div className="text-xs text-gray-400 space-y-1">
              <div>Theme: {themeConfig.name}</div>
              <div>Labels: {labels.length} channels</div>
              <div className="flex items-center gap-1">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: themeConfig.primary_color }}
                />
                {themeConfig.primary_color}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 transition-opacity duration-300"
          onClick={toggleSidebar}
        />
      )}

      {/* CSS Animations */}
  <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateX(10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes pulse-glow {
          0%,
          100% {
            box-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
          }
          50% {
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.6);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
          opacity: 0;
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default ThemeSidebar;
